import type { Side } from './espn'

/**
 * Competition ranking - the "1224" style.
 *
 * Three players tied for second are all second, and the next is fifth, because
 * four people finished ahead of him. Dense ranking would call him third, which
 * tells someone they are doing better than they are. In a pool with an
 * eight-year-old in it, that is the wrong lie to tell.
 *
 * Takes scores already sorted descending and returns a place for each.
 */
export function competitionRank(sortedDescending: readonly number[]): number[] {
  const out: number[] = []
  for (let i = 0; i < sortedDescending.length; i += 1) {
    out.push(i > 0 && sortedDescending[i] === sortedDescending[i - 1] ? out[i - 1]! : i + 1)
  }
  return out
}

/** What one game contributes to a player's race bar. */
export type Segment = 'won' | 'lost' | 'ahead' | 'behind' | 'live' | 'todo'

export interface RaceGame {
  /**
   * Whether THIS game has kicked off — never the board's lock.
   *
   * They were the same field until the board grew one deadline for all ten
   * games. A locked board means the picks are public; it says nothing about a
   * game that kicks off three days later, and reading it as "under way" turned
   * every unplayed game on the bar into a live segment.
   */
  started: boolean
  final: boolean
  winnerSide: Side | 'tie' | 'void' | null
  /** The live score, when there is one. Used only to lean a live segment. */
  homeScore?: number | null
  awayScore?: number | null
}

/**
 * One segment per game, in board order so two players' bars line up.
 *
 * The order matters more than it looks: the whole point of the bar is that you
 * can hold two of them side by side and see where the week was won.
 *
 * It must never leak. A game that has not kicked off is "to come" for
 * everybody, whether or not that player has picked it - the bar is public and
 * the pick is not. This is the same rule the API enforces, restated here so
 * that a caller who hands over a pick it should not have still cannot show it.
 */
export function raceSegments(
  board: readonly RaceGame[],
  picks: readonly (Side | null)[],
): Segment[] {
  return board.map((g, i) => {
    if (!g.started) return 'todo'

    if (!g.final) {
      /*
       * Lean toward whatever this game is about to become.
       *
       * One mint dash for everything in progress says "something is
       * happening", which on a Sunday afternoon is every segment on the bar
       * and therefore no information at all. Ahead leans to the gold it will
       * become, behind leans to the grey.
       *
       * Neutral when it is tied, when no score has arrived, or when this
       * player never picked it - there is nothing to lean toward, and
       * inventing a direction would be worse than saying so.
       */
      const pick = picks[i]
      const h = g.homeScore
      const a = g.awayScore
      if (pick == null || h == null || a == null || h === a) return 'live'
      const leader: Side = h > a ? 'home' : 'away'
      return pick === leader ? 'ahead' : 'behind'
    }

    // A blank on a finished game is a nought, not something still to come.
    return picks[i] !== null && picks[i] === g.winnerSide ? 'won' : 'lost'
  })
}

/**
 * The ordinal a person would actually say: 1ST, 2ND, 3RD, 4TH.
 *
 * The teens are the trap and they are not theoretical here - the pool has
 * twelve players, so 11TH, 12TH and 13TH come up every week. A naive
 * last-digit rule renders them 11ST, 12ND, 13RD.
 */
export function ordinal(n: number): string {
  const suffix =
    n % 100 >= 11 && n % 100 <= 13
      ? 'TH'
      : n % 10 === 1
        ? 'ST'
        : n % 10 === 2
          ? 'ND'
          : n % 10 === 3
            ? 'RD'
            : 'TH'
  return `${n}${suffix}`
}

/** "2ND", or "T-2ND" when the place is shared. */
export function rankLabel(place: number, tied: boolean): string {
  return `${tied ? 'T-' : ''}${ordinal(place)}`
}

/**
 * How far ahead or behind, in words.
 *
 * The leader is measured against the best of everyone ELSE, which is the
 * number that actually matters to them. Two players level at the top are not
 * "leading by 0" and neither of them is behind, so that case gets its own
 * wording rather than a misleading zero.
 */
export function gapText(mine: number, allTotals: readonly number[]): string {
  if (allTotals.length === 0) return 'LEADING'
  const best = Math.max(...allTotals)
  if (mine < best) return `${best - mine} BACK`

  // Level at the top: somebody else is on the same score.
  if (allTotals.filter((t) => t === mine).length > 1) return 'LEVEL AT THE TOP'

  const rest = allTotals.filter((t) => t !== mine)
  return rest.length === 0 ? 'LEADING' : `LEADING BY ${mine - Math.max(...rest)}`
}
